import "./newGhar.scss";
import Sidebar from "../../components/sidebar/Sidebar";
import Navbar from "../../components/navbar/Navbar";
import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import {
  DriveFolderUploadOutlined as UploadIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
} from "@mui/icons-material";
import {
  Button,
  TextField,
  CircularProgress,
  Paper,
  Grid,
  Typography,
  IconButton,
  Chip,
  Box,
  Divider,
} from "@mui/material";
import { useSelector } from "react-redux";

const NewGhar = ({ inputs, title }) => {
  const userIdRedux = useSelector((state) => state.user.userId);
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const navigate = useNavigate();
  const { state } = useLocation();
  const gharData = state?.ghar || {};
  const isEditMode = !!gharData._id;

  // populate formData with gharData if editing
  const [formData, setFormData] = useState({
    userId: gharData.userId || userIdRedux,
    homeName: gharData.homeName || "",
    homePriceLow: gharData.homePriceLow || "",
    homePriceHigh: gharData.homePriceHigh || "",
    homeAddress: gharData.homeAddress || "",
    homePhone: gharData.homePhone || "",
    homeEmail: gharData.homeEmail || "",
    homeDescription: gharData.homeDescription || "",
    homeStars: gharData.homeStars || 5,
    homeType: gharData.homeType || "",
  });

  // Image states
  const [existingImages, setExistingImages] = useState([]);
  const [newImageFiles, setNewImageFiles] = useState([]);
  const [newImagePreviews, setNewImagePreviews] = useState([]);
  const [imagesToDelete, setImagesToDelete] = useState([]);

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [backEndError, setBackEndError] = useState("");

  // Load existing images when in edit mode
  useEffect(() => {
    if (isEditMode && gharData.homeImages) {
      // Handle both string and array formats
      const images = Array.isArray(gharData.homeImages)
        ? gharData.homeImages
        : [gharData.homeImages];

      setExistingImages(images.filter((img) => img)); // Remove empty values
    }
  }, [isEditMode, gharData.homeImages]);

  // Clean up preview URLs on unmount
  useEffect(() => {
    return () => {
      newImagePreviews.forEach((preview) => {
        if (preview.startsWith("blob:")) {
          URL.revokeObjectURL(preview);
        }
      });
    };
  }, [newImagePreviews]);

  const validateForm = () => {
    const errors = {};

    if (!formData.homeName) errors.homeName = "HomeStay name is required.";
    if (!formData.homePriceLow)
      errors.homePriceLow = "Lower Price is required.";
    if (!formData.homePriceHigh)
      errors.homePriceHigh = "Higher Price is required.";
    if (!formData.homeAddress) errors.homeAddress = "Address is required.";

    if (!formData.homePhone) errors.homePhone = "Phone number is required.";
    if (!/^\d{10}$/.test(formData.homePhone))
      errors.homePhone = "Phone number must be 10 digits.";

    if (!formData.homeEmail) errors.homeEmail = "Email is required.";
    if (!/\S+@\S+\.\S+/.test(formData.homeEmail))
      errors.homeEmail = "Email format is invalid.";

    if (!formData.homeDescription)
      errors.homeDescription = "HomeStay Description is required.";

    if (!formData.homeStars) errors.homeStars = "Stars is required.";
    if (!/^\d+$/.test(formData.homeStars))
      errors.homeStars = "Stars must be a number.";

    if (!formData.homeType)
      errors.homeType = "HomeStay/Hotel/Both is required.";

    // Image validation
    const totalImages = existingImages.length + newImageFiles.length;

    if (!isEditMode) {
      if (totalImages === 0) {
        errors.homeImages = "At least 3 images must be uploaded.";
      } else if (totalImages < 3) {
        errors.homeImages = `Please upload ${3 - totalImages} more image(s). Total required: 3`;
      } else if (totalImages > 3) {
        errors.homeImages = "You can only upload exactly 3 images.";
      }
    } else {
      // In edit mode, at least one image should exist
      if (totalImages === 0) {
        errors.homeImages = "At least one image is required.";
      }
    }

    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);

    // Check total images after adding new ones
    const totalAfterAdd =
      existingImages.length + newImageFiles.length + files.length;

    if (!isEditMode && totalAfterAdd > 3) {
      alert(
        `You can only upload exactly 3 images total. You currently have ${existingImages.length + newImageFiles.length} image(s).`,
      );
      return;
    }

    const validImages = files.filter((file) => {
      if (!file.type.startsWith("image/")) {
        alert(`File "${file.name}" is not a valid image.`);
        return false;
      }
      if (file.size > 5 * 1024 * 1024) {
        // 5MB limit
        alert(`File "${file.name}" exceeds the 5MB size limit.`);
        return false;
      }
      return true;
    });

    if (validImages.length > 0) {
      // Add to new image files
      setNewImageFiles((prev) => [...prev, ...validImages]);

      // Generate and add previews
      const previews = validImages.map((file) => URL.createObjectURL(file));
      setNewImagePreviews((prev) => [...prev, ...previews]);
    }
  };

  const handleRemoveExistingImage = (indexToRemove) => {
    const imageToRemove = existingImages[indexToRemove];
    setImagesToDelete((prev) => [...prev, imageToRemove]);
    setExistingImages((prev) =>
      prev.filter((_, index) => index !== indexToRemove),
    );
  };

  const handleRemoveNewImage = (indexToRemove) => {
    // Revoke the blob URL to prevent memory leaks
    if (newImagePreviews[indexToRemove]?.startsWith("blob:")) {
      URL.revokeObjectURL(newImagePreviews[indexToRemove]);
    }

    setNewImageFiles((prev) =>
      prev.filter((_, index) => index !== indexToRemove),
    );
    setNewImagePreviews((prev) =>
      prev.filter((_, index) => index !== indexToRemove),
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setBackEndError("");

    const formDataToSubmit = new FormData();

    // Append form fields
    Object.keys(formData).forEach((key) => {
      if (formData[key] !== undefined && formData[key] !== null) {
        formDataToSubmit.append(key, formData[key]);
      }
    });

    // Append new image files
    newImageFiles.forEach((file) => {
      formDataToSubmit.append("homeImages", file);
    });

    // If in edit mode and there are images to delete, send their URLs
    if (isEditMode && imagesToDelete.length > 0) {
      formDataToSubmit.append("imagesToDelete", JSON.stringify(imagesToDelete));
    }

    // Append existing images that are kept (for reference)
    if (isEditMode && existingImages.length > 0) {
      formDataToSubmit.append("keepImages", JSON.stringify(existingImages));
    }

    const MAIN_URL = isEditMode
      ? `${BASE_URL}/api/ghar/updateGhar/${gharData._id}`
      : `${BASE_URL}/api/ghar/addGhar`;

    try {
      const response = await axios({
        method: isEditMode ? "put" : "post",
        url: MAIN_URL,
        data: formDataToSubmit,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.status === 201 || response.status === 200) {
        // Clean up blob URLs
        newImagePreviews.forEach((preview) => {
          if (preview.startsWith("blob:")) {
            URL.revokeObjectURL(preview);
          }
        });
        navigate("/ghar");
      }
    } catch (error) {
      const errorMessage = error.response?.data?.msg || "An error occurred";
      setBackEndError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="newUser">
      <Sidebar />
      <div className="newUserContainer">
        <Navbar />
        <div
          className="top"
          style={{ display: "flex", alignItems: "center", gap: "1rem" }}
        >
          <h1>{isEditMode ? "Edit HomeStay" : title}</h1>
          {isEditMode && (
            <Chip
              label={`ID: ${gharData._id}`}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
        </div>
        <div className="bottom">
          <form onSubmit={handleSubmit}>
            <Paper
              elevation={3}
              style={{ padding: "2rem", marginBottom: "2rem" }}
            >
              <Typography variant="h6" gutterBottom>
                <ImageIcon
                  style={{ marginRight: "0.5rem", verticalAlign: "middle" }}
                />
                HomeStay Images
              </Typography>
              <Divider style={{ marginBottom: "1.5rem" }} />

              {/* Image Upload Section */}
              <Box mb={3}>
                <input
                  id="homeImages"
                  name="homeImages"
                  type="file"
                  multiple
                  hidden
                  accept="image/*"
                  onChange={handleFileChange}
                />
                <label htmlFor="homeImages">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<UploadIcon />}
                    style={{ marginBottom: "1rem" }}
                  >
                    {isEditMode ? "Add More Images" : "Upload Images"}
                  </Button>
                </label>

                <Typography
                  variant="caption"
                  display="block"
                  color="textSecondary"
                >
                  {isEditMode
                    ? "You can add new images. Existing images will be preserved unless removed."
                    : "Please upload exactly 3 images (max 5MB each)"}
                </Typography>
              </Box>

              {/* Existing Images (Edit Mode) */}
              {isEditMode && existingImages.length > 0 && (
                <Box mb={3}>
                  <Typography variant="subtitle2" gutterBottom>
                    Existing Images:
                  </Typography>
                  <Grid container spacing={2}>
                    {existingImages.map((image, index) => (
                      <Grid item key={`existing-${index}`}>
                        <Box position="relative">
                          <img
                            src={image}
                            alt={`Existing ${index + 1}`}
                            style={{
                              width: "120px",
                              height: "120px",
                              objectFit: "cover",
                              borderRadius: "8px",
                              border: "2px solid #e0e0e0",
                            }}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src =
                                "https://icon-library.com/images/no-image-icon/no-image-icon-0.jpg";
                            }}
                          />
                          <IconButton
                            size="small"
                            style={{
                              position: "absolute",
                              top: -8,
                              right: -8,
                              backgroundColor: "#ff4444",
                              color: "white",
                              padding: "4px",
                            }}
                            onClick={() => handleRemoveExistingImage(index)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                          <Chip
                            label="Existing"
                            size="small"
                            style={{
                              position: "absolute",
                              bottom: -8,
                              left: "50%",
                              transform: "translateX(-50%)",
                              backgroundColor: "#4caf50",
                              color: "white",
                              fontSize: "0.7rem",
                            }}
                          />
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* New Image Previews */}
              {newImagePreviews.length > 0 && (
                <Box mb={3}>
                  <Typography variant="subtitle2" gutterBottom>
                    New Images to Upload:
                  </Typography>
                  <Grid container spacing={2}>
                    {newImagePreviews.map((preview, index) => (
                      <Grid item key={`new-${index}`}>
                        <Box position="relative">
                          <img
                            src={preview}
                            alt={`New ${index + 1}`}
                            style={{
                              width: "120px",
                              height: "120px",
                              objectFit: "cover",
                              borderRadius: "8px",
                              border: "2px solid #2196f3",
                            }}
                          />
                          <IconButton
                            size="small"
                            style={{
                              position: "absolute",
                              top: -8,
                              right: -8,
                              backgroundColor: "#ff4444",
                              color: "white",
                              padding: "4px",
                            }}
                            onClick={() => handleRemoveNewImage(index)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                          <Chip
                            label="New"
                            size="small"
                            style={{
                              position: "absolute",
                              bottom: -8,
                              left: "50%",
                              transform: "translateX(-50%)",
                              backgroundColor: "#2196f3",
                              color: "white",
                              fontSize: "0.7rem",
                            }}
                          />
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* Image Count Info */}
              <Box mt={2}>
                <Typography variant="body2" color="textSecondary">
                  Total Images: {existingImages.length + newImageFiles.length}
                  {isEditMode && (
                    <span style={{ marginLeft: "1rem", color: "#666" }}>
                      (Existing: {existingImages.length} | New:{" "}
                      {newImageFiles.length})
                    </span>
                  )}
                </Typography>
                {errors.homeImages && (
                  <Typography color="error" variant="caption">
                    {errors.homeImages}
                  </Typography>
                )}
              </Box>
            </Paper>

            <Paper elevation={3} style={{ padding: "2rem" }}>
              <Typography variant="h6" gutterBottom>
                HomeStay Details
              </Typography>
              <Divider style={{ marginBottom: "1.5rem" }} />

              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    name="homeName"
                    label="HomeStay Name *"
                    value={formData.homeName}
                    onChange={handleChange}
                    error={Boolean(errors.homeName)}
                    helperText={errors.homeName}
                    required
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    name="homeType"
                    label="HomeStay Type *"
                    value={formData.homeType}
                    onChange={handleChange}
                    error={Boolean(errors.homeType)}
                    helperText={errors.homeType}
                    placeholder="HomeStay / Hotel / Both"
                    required
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    name="homePriceLow"
                    label="Lowest Price (NPR) *"
                    type="number"
                    value={formData.homePriceLow}
                    onChange={handleChange}
                    error={Boolean(errors.homePriceLow)}
                    helperText={errors.homePriceLow}
                    required
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    name="homePriceHigh"
                    label="Highest Price (NPR) *"
                    type="number"
                    value={formData.homePriceHigh}
                    onChange={handleChange}
                    error={Boolean(errors.homePriceHigh)}
                    helperText={errors.homePriceHigh}
                    required
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    name="homeAddress"
                    label="Address *"
                    value={formData.homeAddress}
                    onChange={handleChange}
                    error={Boolean(errors.homeAddress)}
                    helperText={errors.homeAddress}
                    required
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    name="homeStars"
                    label="Stars *"
                    type="number"
                    value={formData.homeStars}
                    onChange={handleChange}
                    error={Boolean(errors.homeStars)}
                    helperText={errors.homeStars}
                    InputProps={{
                      inputProps: { min: 1, max: 5 },
                    }}
                    required
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    name="homeEmail"
                    label="Email *"
                    type="email"
                    value={formData.homeEmail}
                    onChange={handleChange}
                    error={Boolean(errors.homeEmail)}
                    helperText={errors.homeEmail}
                    required
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    name="homePhone"
                    label="Phone Number *"
                    value={formData.homePhone}
                    onChange={handleChange}
                    error={Boolean(errors.homePhone)}
                    helperText={errors.homePhone}
                    type="tel"
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="homeDescription"
                    label="Description *"
                    value={formData.homeDescription}
                    onChange={handleChange}
                    multiline
                    rows={4}
                    error={Boolean(errors.homeDescription)}
                    helperText={errors.homeDescription}
                    required
                  />
                </Grid>
              </Grid>
            </Paper>

            {backEndError && (
              <Typography color="error" style={{ marginTop: "1rem" }}>
                {backEndError}
              </Typography>
            )}

            <Box mt={3} display="flex" justifyContent="flex-start" gap={2}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
                style={{ minWidth: "120px" }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : isEditMode ? (
                  "Update"
                ) : (
                  "Save"
                )}
              </Button>

              <Button variant="outlined" onClick={() => navigate("/ghar")}>
                Cancel
              </Button>
            </Box>
          </form>
        </div>
      </div>
    </div>
  );
};

export default NewGhar;
